/**
 * КАРДИНАЛЬНОЕ РЕШЕНИЕ: Определение API URL в runtime на основе текущего домена
 * Не зависит от переменных окружения, которые могут не работать правильно
 */
const getApiBaseUrl = (): string => {
  // Если мы в браузере, определяем по текущему домену
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    
    // Явная проверка на localhost варианты
    const isLocalhost = hostname === 'localhost' || 
                       hostname === '127.0.0.1' || 
                       hostname === '::1' ||
                       hostname === '0.0.0.0' ||
                       hostname.startsWith('192.168.') ||
                       hostname.startsWith('10.') ||
                       hostname.startsWith('172.');
    
    if (isLocalhost) {
      const url = 'http://localhost:3001/api';
      return url;
    }
    
    // Для всех остальных случаев (продакшн, тестовый сервер) - используем относительный путь
    // Nginx будет проксировать /api на backend
    const url = '/api';
    return url;
  }
  
  // Если не в браузере (SSR или тесты), проверяем переменные окружения
  const envUrl = import.meta.env.VITE_API_URL;
  
  if (envUrl) {
    // Если URL начинается с /, это относительный путь
    if (envUrl.startsWith('/')) {
      return envUrl;
    }
    // Если это полный URL, возвращаем как есть
    if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
      return envUrl;
    }
  }
  
  // Fallback: определяем по режиму сборки
  const isDev = import.meta.env.DEV === true || import.meta.env.MODE === 'development';
  return isDev ? 'http://localhost:3001/api' : '/api';
};

// Определяем API_BASE_URL динамически при каждом вызове
// Это гарантирует, что мы всегда получаем актуальное значение
export const getApiBaseUrlDynamic = (): string => {
  return getApiBaseUrl();
};

/**
 * Базовый URL для статических файлов (uploads).
 * Файлы отдаются по /api/uploads, чтобы за одним прокси (Nginx location /api) работали и API, и картинки.
 * Localhost: полный URL бэкенда с /api. Иначе — относительный /api (same-origin).
 */
export const getUploadsBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' ||
                       hostname === '127.0.0.1' ||
                       hostname === '::1' ||
                       hostname === '0.0.0.0' ||
                       hostname.startsWith('192.168.') ||
                       hostname.startsWith('10.') ||
                       hostname.startsWith('172.');
    if (isLocalhost) {
      return 'http://localhost:3001/api';
    }
    return '/api';
  }
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && (envUrl.startsWith('http://') || envUrl.startsWith('https://'))) {
    return envUrl.replace(/\/api\/?$/, '') + '/api';
  }
  const isDev = import.meta.env.DEV === true || import.meta.env.MODE === 'development';
  return isDev ? 'http://localhost:3001/api' : '/api';
};

/**
 * Нормализует URL картинки (cover, uploads): /uploads/... → /api/uploads/...
 * Нужно, чтобы работало при старом бэкенде или кэше, отдающем /uploads без /api.
 */
export const normalizeUploadImageUrl = (url: string | undefined): string | undefined => {
  if (!url || !url.trim()) return undefined;
  const trimmed = url.trim();
  if (trimmed.startsWith('/api/uploads')) return trimmed;
  if (trimmed.startsWith('/uploads')) return '/api' + trimmed;
  if (trimmed.startsWith('http')) {
    try {
      const u = new URL(trimmed);
      if (typeof window !== 'undefined' && window.location && u.origin === window.location.origin && u.pathname.startsWith('/uploads') && !u.pathname.startsWith('/api/uploads'))
        return '/api' + u.pathname;
    } catch {
      // ignore
    }
  }
  return trimmed;
};

// Для обратной совместимости, но лучше использовать getApiBaseUrlDynamic()
export const API_BASE_URL = getApiBaseUrl();

// Добавим функцию проверки доступности API (использует динамический URL)
export const checkApiAvailability = async (): Promise<boolean> => {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const apiUrl = getApiBaseUrlDynamic();

        const response = await fetch(`${apiUrl}/health`, {
            method: 'GET',
            signal: controller.signal
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        console.error('API availability check failed:', error);
        return false;
    }
};
export const API_TIMEOUT = 30000;

export const API_ENDPOINTS = {
    FEDERATIONS: {
        BASE: '/federations',
        GET: (id: number) => `/federations/${id}`,
        CREATE: '/federations',
        UPDATE: (id: number) => `/federations/${id}`,  // This is correct
        DELETE: (id: number) => `/federations/${id}`,
    },
    COACHES: {
        BASE: '/coaches',
        GET: (id: number) => `/coaches/${id}`,
        CREATE: '/coaches',
        UPDATE: (id: number) => `/coaches/${id}`,
        DELETE: (id: number) => `/coaches/${id}`,
        BLOCK: (id: number) => `/coaches/${id}/block`,
        UNBLOCK: (id: number) => `/coaches/${id}/unblock`
    },
    CLUBS: {
        BASE: '/clubs',
        PARTICIPANTS: {
            BASE: (clubId: number) => `/clubs/${clubId}/participants`,
            REMOVE: (participantId: number) => `/clubs/participants/${participantId}`,
            ADD: (clubId: number, participantId: number) => `/clubs/${clubId}/participants/${participantId}`,
        }
    }
} as const;

export default API_ENDPOINTS;

export const API_CONFIG = {
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: API_TIMEOUT,
    retries: 2,
    retryDelay: 1000
};
